import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from '../bizsuite-app';
import SearchableSelect from '../components/SearchableSelect';
import ActionsEditor from '../components/ActionsEditor';
import { formatCurrency } from '../utils/currency';
import { formatDate, todayISO } from '../utils/date';
import { toast } from '../utils/toast';

/**
 * Opportunities — Master-Detail layout.
 *
 * Master pane (left):  filterable list of opportunities, ~320px wide.
 *                      Each row shows name + customer + stage pill + value.
 * Detail pane (right): tabbed editor for the selected opportunity:
 *                      General · Pricing · Pipeline · Actions · Description.
 *
 * The Actions tab embeds <ActionsEditor entityType="opportunity" .../>
 * which is the same component reused for Lead / Customer in their pages.
 *
 * Backend endpoints (unchanged from prior version):
 *   GET   /api/crm/opportunities
 *   POST  /api/crm/opportunities
 *   PUT   /api/crm/opportunities/:id
 *   DELETE /api/crm/opportunities/:id
 */

const STAGE_OPTIONS = ['Qualification', 'Discovery', 'Proposal', 'Negotiation', 'Closed Won', 'Closed Lost'];
const SERVICE_TYPES = [
  'SAP Implementation', 'SAP AMS', 'SAP Migration', 'IT Staffing',
  'Cloud Services', 'Consulting', 'Other',
];
const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED'];

const NEW_SENTINEL = '_new';
const EMPTY_OPP = {
  name: '', lead_id: '', customer_id: '', contract_id: '',
  stage: 'Qualification', service_type: '',
  estimated_value: '', currency: 'INR', probability_pct: '20', expected_start: '',
  description: '', assigned_to: '',
  // Lead-parity fields — copied over when converting a Lead, but also
  // editable directly on Opportunities that originate elsewhere.
  contact_name: '', contact_email: '', contact_phone: '',
  source: '', vertical: '', industry: '', sub_vertical: '',
  legal_entity: '', company_size: '', management_type: '',
  linkedin_url: '', job_title: '', notes: '',
};

// Tabs shown in the detail header. History tab fetches from /api/crm/opportunities/:id/history.
const TABS = ['General', 'Pricing', 'Pipeline', 'Actions', 'Description', 'History'];

export default function Opportunities() {
  const [opportunities, setOpportunities] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_OPP });
  const [editing, setEditing] = useState(false);    // toggle between read-only and edit mode
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('General');
  const [filter, setFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [loading, setLoading] = useState(true);

  // History tab state — list + new-comment input. Loaded lazily when the
  // user clicks the tab so we don't fetch audit_log on every selection.
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [newComment, setNewComment] = useState('');

  // ─── Data load ────────────────────────────────────────────────
  // Lead → Opportunity convert flow: Leads.jsx writes a JSON snapshot to
  // sessionStorage under bizsuite:opportunityPrefill, then nav-routes here.
  // We pick it up once on mount, open the New form prefilled from it, and
  // clear the storage so a refresh doesn't re-apply the prefill forever.
  const [pendingPrefill, setPendingPrefill] = useState(() => {
    try {
      const raw = sessionStorage.getItem('bizsuite:opportunityPrefill');
      if (!raw) return null;
      sessionStorage.removeItem('bizsuite:opportunityPrefill');
      return JSON.parse(raw);
    } catch (_) { return null; }
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [oppR, custR, leadR, ctrR, empR] = await Promise.allSettled([
        apiFetch('/api/crm/opportunities'),
        apiFetch('/api/customers?limit=500'),
        apiFetch('/api/crm/leads?limit=500'),
        apiFetch('/api/contracts?limit=500'),
        apiFetch('/api/employees?limit=500'),
      ]);
      // Each endpoint can return one of three response shapes — accept all
      // ({ data: [...] } / { customers: [...] } / [...] directly). The
      // bare-array fallback caught the bug where Lead/Customer dropdowns
      // appeared empty even though the underlying tables had records.
      const pick = (d, key) => d?.data ?? d?.[key] ?? (Array.isArray(d) ? d : []);
      if (oppR.status === 'fulfilled' && oppR.value.ok) {
        const d = await oppR.value.json();
        setOpportunities(pick(d, 'opportunities'));
      }
      if (custR.status === 'fulfilled' && custR.value.ok) {
        const d = await custR.value.json();
        setCustomers(pick(d, 'customers'));
      }
      if (leadR.status === 'fulfilled' && leadR.value.ok) {
        const d = await leadR.value.json();
        const all = pick(d, 'leads');
        // Don't show disqualified leads in the picker
        setLeads(all.filter(l => (l.status || '').toLowerCase() !== 'disqualified'));
      }
      if (ctrR.status === 'fulfilled' && ctrR.value.ok) {
        const d = await ctrR.value.json();
        setContracts(pick(d, 'contracts'));
      }
      if (empR.status === 'fulfilled' && empR.value.ok) {
        const d = await empR.value.json();
        setEmployees(pick(d, 'employees'));
      }
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Auto-select first opportunity when list loads, but don't override
  // user-selected pick. If a Lead-convert prefill is pending, that takes
  // precedence — we open the New form and apply the prefill instead of
  // auto-selecting an existing record.
  useEffect(() => {
    if (pendingPrefill) {
      setSelectedId(NEW_SENTINEL);
      return;
    }
    if (!selectedId && opportunities.length) setSelectedId(opportunities[0].id);
  }, [opportunities, selectedId, pendingPrefill]);

  // After the New form's empty state is set up by the form-sync useEffect,
  // overlay the prefill. Done in a separate effect so we run AFTER the
  // EMPTY_OPP reset and don't get clobbered by it.
  useEffect(() => {
    if (selectedId !== NEW_SENTINEL || !pendingPrefill) return;
    setForm(prev => ({
      ...prev,
      ...pendingPrefill,
      lead_id: pendingPrefill.leadId || prev.lead_id || '',
    }));
    // One-shot — clear so subsequent opens of New form start fresh.
    setPendingPrefill(null);
  }, [selectedId, pendingPrefill]);

  // Sync form when selection changes (and we're not in the middle of creating new)
  useEffect(() => {
    if (selectedId === NEW_SENTINEL) {
      setForm({ ...EMPTY_OPP, expected_start: todayISO() });
      setEditing(true);
      setTab('General');
      return;
    }
    const opp = opportunities.find(o => o.id === selectedId);
    if (!opp) { setForm({ ...EMPTY_OPP }); return; }
    setForm({
      name: opp.name || '',
      lead_id: opp.lead_id || '',
      customer_id: opp.customer_id || '',
      contract_id: opp.contract_id || '',
      stage: opp.stage || 'Qualification',
      service_type: opp.service_type || '',
      estimated_value: opp.estimated_value != null ? String(opp.estimated_value) : '',
      currency: opp.currency || 'INR',
      probability_pct: opp.probability_pct != null ? String(opp.probability_pct) : '20',
      expected_start: opp.expected_start ? opp.expected_start.substring(0, 10) : '',
      description: opp.description || '',
      assigned_to: opp.assigned_to || '',
      // Lead-parity fields. Default to '' so controlled inputs stay defined.
      contact_name:    opp.contact_name    || '',
      contact_email:   opp.contact_email   || '',
      contact_phone:   opp.contact_phone   || '',
      source:          opp.source          || '',
      vertical:        opp.vertical        || '',
      industry:        opp.industry        || '',
      sub_vertical:    opp.sub_vertical    || '',
      legal_entity:    opp.legal_entity    || '',
      company_size:    opp.company_size    || '',
      management_type: opp.management_type || '',
      linkedin_url:    opp.linkedin_url    || '',
      job_title:       opp.job_title       || '',
      notes:           opp.notes           || '',
    });
    setEditing(false);
    if (tab === 'Actions') setTab('Actions'); else setTab('General');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, opportunities]);

  // ─── List filtering ──────────────────────────────────────────
  const filteredList = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return opportunities.filter(o => {
      if (stageFilter && o.stage !== stageFilter) return false;
      if (!q) return true;
      const customer = customers.find(c => c.id === o.customer_id)?.name || '';
      const blob = [o.name, customer, o.service_type, o.stage].filter(Boolean).join(' ').toLowerCase();
      return blob.includes(q);
    });
  }, [opportunities, customers, filter, stageFilter]);

  // ─── CRUD ────────────────────────────────────────────────────
  function startNew() { setSelectedId(NEW_SENTINEL); }

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  async function save() {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.lead_id && !form.customer_id && !form.contract_id) {
      toast.error('Pick a Lead, Customer, or Contract'); return;
    }
    setSaving(true);
    try {
      const body = {
        name: form.name,
        leadId: form.lead_id || null,
        customerId: form.customer_id || null,
        contractId: form.contract_id || null,
        stage: form.stage,
        serviceType: form.service_type || null,
        estimatedValue: form.estimated_value ? parseFloat(form.estimated_value) : null,
        currency: form.currency,
        probabilityPct: form.probability_pct ? parseInt(form.probability_pct, 10) : null,
        expectedStart: form.expected_start || null,
        description: form.description || null,
        assignedTo: form.assigned_to || null,
        // Lead-parity fields — backend stores them as their own columns so
        // the lead's contact details, classification, and enrichment data
        // travel with the opportunity through its full lifecycle.
        contactName:    form.contact_name    || null,
        contactEmail:   form.contact_email   || null,
        contactPhone:   form.contact_phone   || null,
        source:         form.source          || null,
        vertical:       form.vertical        || null,
        industry:       form.industry        || null,
        subVertical:    form.sub_vertical    || null,
        legalEntity:    form.legal_entity    || null,
        companySize:    form.company_size    || null,
        managementType: form.management_type || null,
        linkedinUrl:    form.linkedin_url    || null,
        jobTitle:       form.job_title       || null,
        notes:          form.notes           || null,
      };
      const isNew = selectedId === NEW_SENTINEL;
      const url = isNew ? '/api/crm/opportunities' : `/api/crm/opportunities/${selectedId}`;
      const method = isNew ? 'POST' : 'PUT';
      const r = await apiFetch(url, { method, body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || 'Save failed'); setSaving(false); return; }
      toast.success(isNew ? 'Opportunity created' : 'Saved');
      const saved = d.data || d.opportunity || d;
      // Reload list and select the saved record
      await load();
      if (saved && saved.id) setSelectedId(saved.id);
      setEditing(false);
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  }

  // ─── History helpers ──────────────────────────────────────────
  const loadHistory = useCallback(async (id) => {
    if (!id || id === NEW_SENTINEL) { setHistory([]); return; }
    setHistoryLoading(true);
    try {
      const r = await apiFetch(`/api/crm/opportunities/${id}/history`);
      const d = await r.json().catch(() => ({}));
      if (r.ok) setHistory(d.history || []);
    } finally { setHistoryLoading(false); }
  }, []);

  // Auto-fetch history when the user opens the tab on an existing opp.
  useEffect(() => {
    if (tab === 'History' && selectedId && selectedId !== NEW_SENTINEL) {
      loadHistory(selectedId);
    }
  }, [tab, selectedId, loadHistory]);

  async function postComment() {
    const text = newComment.trim();
    if (!text) return;
    if (!selectedId || selectedId === NEW_SENTINEL) return;
    const r = await apiFetch(`/api/crm/opportunities/${selectedId}/notes`, {
      method: 'POST', body: JSON.stringify({ text }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(d.error || 'Could not post comment'); return; }
    setNewComment('');
    loadHistory(selectedId);
    toast.success('Comment added');
  }

  async function del() {
    if (!selectedId || selectedId === NEW_SENTINEL) return;
    if (!window.confirm(`Delete "${form.name}"? This cannot be undone.`)) return;
    const r = await apiFetch(`/api/crm/opportunities/${selectedId}`, { method: 'DELETE' });
    if (!r.ok) { toast.error('Delete failed'); return; }
    toast.success('Deleted');
    setSelectedId(null);
    await load();
  }

  // ─── Helpers for read-only display ───────────────────────────
  const customerName = id => customers.find(c => c.id === id)?.name || '—';
  const leadName = id => {
    const l = leads.find(x => x.id === id);
    return l ? (l.company_name || l.contact_name || '—') : '—';
  };
  const contractName = id => contracts.find(c => c.id === id)?.contract_number || contracts.find(c => c.id === id)?.name || '—';
  const employeeName = id => {
    const e = employees.find(x => x.id === id);
    return e ? [e.first_name, e.last_name].filter(Boolean).join(' ') : '—';
  };

  const isNew = selectedId === NEW_SENTINEL;
  const selected = isNew ? null : opportunities.find(o => o.id === selectedId);

  // ─── Render ──────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 60px)', background: '#0B1020' }}>
      {/* ─────────── Master pane ─────────── */}
      <aside style={{ width: 340, flexShrink: 0, borderRight: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: 14, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          {/* No <h1> here — the app shell already shows the page title. */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 10 }}>
            <button onClick={startNew} style={btnAdd}>+ New</button>
          </div>
          <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Search…" style={inp} />
          <div style={{ marginTop: 8 }}>
            <SearchableSelect
              value={stageFilter}
              onChange={setStageFilter}
              options={[{ value: '', label: 'All stages' }, ...STAGE_OPTIONS.map(s => ({ value: s, label: s }))]}
              placeholder="All stages"
            />
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading && <div style={{ padding: 16, color: '#94A3B8' }}>Loading…</div>}
          {!loading && filteredList.length === 0 && (
            <div style={{ padding: 16, color: '#64748B', fontSize: 12.5 }}>No opportunities match.</div>
          )}
          {filteredList.map(o => (
            <ListRow key={o.id} opp={o} customer={customerName(o.customer_id)}
              selected={o.id === selectedId} onClick={() => setSelectedId(o.id)} />
          ))}
        </div>
      </aside>

      {/* ─────────── Detail pane ─────────── */}
      <main style={{ flex: 1, overflowY: 'auto' }}>
        {!selectedId && (
          <div style={{ padding: 60, textAlign: 'center', color: '#64748B' }}>
            Select an opportunity from the list, or click <strong>+ New</strong>.
          </div>
        )}
        {selectedId && (
          // The `key` here is the critical bit — when the user clicks a
          // different opportunity in the master list, React unmounts the
          // entire detail subtree and remounts it with fresh state. That
          // sidesteps the class of bugs where a child component's local
          // state (e.g. ActionsEditor's loaded actions list, or History's
          // cached list) doesn't react to id-prop changes correctly.
          <div key={selectedId} style={{ padding: 20, maxWidth: 1200 }}>
            {/* Header row — editable name + action buttons */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                {editing ? (
                  <input value={form.name} onChange={e => set('name', e.target.value)}
                    placeholder="Opportunity name" style={{ ...inp, fontSize: 18, fontWeight: 600 }} />
                ) : (
                  <h1 style={{ margin: 0, fontSize: 20 }}>{form.name || '(untitled)'}</h1>
                )}
                {!editing && selected && (
                  <div style={{ marginTop: 4, color: '#94A3B8', fontSize: 12.5 }}>
                    {customerName(selected.customer_id)} · <StagePill stage={selected.stage} /> ·
                    {selected.estimated_value != null && (
                      <span style={{ marginLeft: 6 }}>
                        {formatCurrency(selected.estimated_value, selected.currency || 'INR')}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {!editing && !isNew && (
                  <>
                    <button onClick={() => setEditing(true)} style={btnSecondary}>Edit</button>
                    <button onClick={del} style={btnDanger}>Delete</button>
                  </>
                )}
                {editing && (
                  <>
                    <button onClick={() => { setEditing(false); if (isNew) setSelectedId(null); }} style={btnSecondary}>Cancel</button>
                    <button onClick={save} disabled={saving} style={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
                  </>
                )}
              </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: 14 }}>
              {TABS.map(t => {
                // Don't show Actions tab when creating new (need an id first)
                if (t === 'Actions' && isNew) return null;
                return (
                  <button key={t} onClick={() => setTab(t)} style={{
                    padding: '8px 14px', border: 'none', background: 'transparent', cursor: 'pointer',
                    color: tab === t ? '#6FE6FF' : '#94A3B8',
                    borderBottom: tab === t ? '2px solid #00E0FF' : '2px solid transparent',
                    fontSize: 13, fontWeight: 600,
                  }}>{t}</button>
                );
              })}
            </div>

            {/* Tab content */}
            {tab === 'General' && (
              <Section>
                <Grid2>
                  <Field label="Lead">
                    {editing ? (
                      <SearchableSelect value={form.lead_id} onChange={v => set('lead_id', v)}
                        options={[{ value: '', label: '—' }, ...leads.map(l => ({ value: l.id, label: l.company_name || l.contact_name }))]}
                        placeholder="Pick a lead" />
                    ) : <ReadOnly>{leadName(form.lead_id)}</ReadOnly>}
                  </Field>
                  <Field label="Customer">
                    {editing ? (
                      <SearchableSelect value={form.customer_id} onChange={v => set('customer_id', v)}
                        options={[{ value: '', label: '—' }, ...customers.map(c => ({ value: c.id, label: c.name }))]}
                        placeholder="Pick a customer" />
                    ) : <ReadOnly>{customerName(form.customer_id)}</ReadOnly>}
                  </Field>
                  <Field label="Contract (optional)">
                    {editing ? (
                      <SearchableSelect value={form.contract_id} onChange={v => set('contract_id', v)}
                        options={[{ value: '', label: '—' }, ...contracts.map(c => ({ value: c.id, label: c.contract_number || c.name }))]}
                        placeholder="Pick a contract" />
                    ) : <ReadOnly>{contractName(form.contract_id)}</ReadOnly>}
                  </Field>
                  <Field label="Service Type">
                    {editing ? (
                      <SearchableSelect value={form.service_type} onChange={v => set('service_type', v)}
                        options={[{ value: '', label: '—' }, ...SERVICE_TYPES.map(s => ({ value: s, label: s }))]}
                        placeholder="Pick a service" />
                    ) : <ReadOnly>{form.service_type || '—'}</ReadOnly>}
                  </Field>
                  <Field label="Assigned To">
                    {editing ? (
                      <SearchableSelect value={form.assigned_to} onChange={v => set('assigned_to', v)}
                        options={[{ value: '', label: '—' }, ...employees.map(e => ({
                          value: e.id, label: [e.first_name, e.last_name].filter(Boolean).join(' '),
                        }))]}
                        placeholder="Pick a user" />
                    ) : <ReadOnly>{employeeName(form.assigned_to)}</ReadOnly>}
                  </Field>
                  <Field label="Expected Start">
                    {editing ? (
                      <input type="date" value={form.expected_start} onChange={e => set('expected_start', e.target.value)} style={inp} />
                    ) : <ReadOnly>{form.expected_start ? formatDate(form.expected_start) : '—'}</ReadOnly>}
                  </Field>
                </Grid2>

                {/* ─── Contact (carried from the source Lead, editable here) ─── */}
                <div style={{ marginTop: 16, marginBottom: 8, fontSize: 11, letterSpacing: '0.6px', textTransform: 'uppercase', color: '#94A3B8', fontWeight: 700 }}>Contact</div>
                <Grid2>
                  <Field label="Contact name">
                    {editing
                      ? <input value={form.contact_name} onChange={e => set('contact_name', e.target.value)} style={inp} placeholder="Jane Doe" />
                      : <ReadOnly>{form.contact_name || '—'}</ReadOnly>}
                  </Field>
                  <Field label="Contact email">
                    {editing
                      ? <input type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} style={inp} placeholder="jane@acme.com" />
                      : <ReadOnly>{form.contact_email || '—'}</ReadOnly>}
                  </Field>
                  <Field label="Contact phone">
                    {editing
                      ? <input value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)} style={inp} placeholder="+91 ..." />
                      : <ReadOnly>{form.contact_phone || '—'}</ReadOnly>}
                  </Field>
                  <Field label="Job Title (Designation - Department)">
                    {editing
                      ? <input value={form.job_title} onChange={e => set('job_title', e.target.value)} style={inp} placeholder="VP of Sales - Sales" />
                      : <ReadOnly>{form.job_title || '—'}</ReadOnly>}
                  </Field>
                  <Field label="LinkedIn URL">
                    {editing ? (
                      <input value={form.linkedin_url} onChange={e => set('linkedin_url', e.target.value)} style={inp} placeholder="https://www.linkedin.com/in/..." />
                    ) : (
                      <ReadOnly>
                        {form.linkedin_url && /^https?:\/\//i.test(form.linkedin_url)
                          ? <a href={form.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ color: '#6FE6FF' }}>Open profile ↗</a>
                          : (form.linkedin_url || '—')}
                      </ReadOnly>
                    )}
                  </Field>
                  <Field label="Source">
                    {editing
                      ? <input value={form.source} onChange={e => set('source', e.target.value)} style={inp} placeholder="Referral / Cold call / ..." />
                      : <ReadOnly>{form.source || '—'}</ReadOnly>}
                  </Field>
                </Grid2>

                {/* ─── Company classification (vertical / industry / size) ─── */}
                <div style={{ marginTop: 16, marginBottom: 8, fontSize: 11, letterSpacing: '0.6px', textTransform: 'uppercase', color: '#94A3B8', fontWeight: 700 }}>Company classification</div>
                <Grid2>
                  <Field label="Vertical">
                    {editing
                      ? <input value={form.vertical} onChange={e => set('vertical', e.target.value)} style={inp} />
                      : <ReadOnly>{form.vertical || '—'}</ReadOnly>}
                  </Field>
                  <Field label="Industry">
                    {editing
                      ? <input value={form.industry} onChange={e => set('industry', e.target.value)} style={inp} />
                      : <ReadOnly>{form.industry || '—'}</ReadOnly>}
                  </Field>
                  <Field label="Sub-vertical">
                    {editing
                      ? <input value={form.sub_vertical} onChange={e => set('sub_vertical', e.target.value)} style={inp} />
                      : <ReadOnly>{form.sub_vertical || '—'}</ReadOnly>}
                  </Field>
                  <Field label="Legal entity">
                    {editing
                      ? <input value={form.legal_entity} onChange={e => set('legal_entity', e.target.value)} style={inp} />
                      : <ReadOnly>{form.legal_entity || '—'}</ReadOnly>}
                  </Field>
                  <Field label="Company size">
                    {editing
                      ? <input value={form.company_size} onChange={e => set('company_size', e.target.value)} style={inp} />
                      : <ReadOnly>{form.company_size || '—'}</ReadOnly>}
                  </Field>
                  <Field label="Management type">
                    {editing
                      ? <input value={form.management_type} onChange={e => set('management_type', e.target.value)} style={inp} />
                      : <ReadOnly>{form.management_type || '—'}</ReadOnly>}
                  </Field>
                </Grid2>
              </Section>
            )}

            {tab === 'Pricing' && (
              <Section>
                <Grid2>
                  <Field label="Estimated Value">
                    {editing ? (
                      <input type="number" value={form.estimated_value}
                        onChange={e => set('estimated_value', e.target.value)} style={inp} placeholder="0" />
                    ) : (
                      <ReadOnly>
                        {form.estimated_value
                          ? formatCurrency(parseFloat(form.estimated_value), form.currency)
                          : '—'}
                      </ReadOnly>
                    )}
                  </Field>
                  <Field label="Currency">
                    {editing ? (
                      <SearchableSelect value={form.currency} onChange={v => set('currency', v)}
                        options={CURRENCIES.map(c => ({ value: c, label: c }))} />
                    ) : <ReadOnly>{form.currency}</ReadOnly>}
                  </Field>
                  <Field label="Probability %">
                    {editing ? (
                      <input type="number" min={0} max={100} value={form.probability_pct}
                        onChange={e => set('probability_pct', e.target.value)} style={inp} />
                    ) : <ReadOnly>{form.probability_pct ? `${form.probability_pct}%` : '—'}</ReadOnly>}
                  </Field>
                  <Field label="Weighted Value (computed)">
                    <ReadOnly>
                      {form.estimated_value && form.probability_pct
                        ? formatCurrency(parseFloat(form.estimated_value) * parseFloat(form.probability_pct) / 100, form.currency)
                        : '—'}
                    </ReadOnly>
                  </Field>
                </Grid2>
              </Section>
            )}

            {tab === 'Pipeline' && (
              <Section>
                <Grid2>
                  <Field label="Stage">
                    {editing ? (
                      <SearchableSelect value={form.stage} onChange={v => set('stage', v)}
                        options={STAGE_OPTIONS.map(s => ({ value: s, label: s }))} />
                    ) : <ReadOnly><StagePill stage={form.stage} /></ReadOnly>}
                  </Field>
                </Grid2>
              </Section>
            )}

            {tab === 'Actions' && !isNew && (
              <Section>
                <div style={{ marginBottom: 10, color: '#94A3B8', fontSize: 12 }}>
                  Action checklist for this opportunity. Each row with assignees + due date/time
                  will email the assignees and surface in their Inbox when due.
                </div>
                <ActionsEditor entityType="opportunity" entityId={selected?.id} stage={form.stage} />
              </Section>
            )}

            {tab === 'Description' && (
              <Section>
                {editing ? (
                  <textarea value={form.description} onChange={e => set('description', e.target.value)}
                    rows={10} style={{ ...inp, fontFamily: 'inherit', resize: 'vertical' }}
                    placeholder="Describe the opportunity, scope, key stakeholders, competitive notes…" />
                ) : (
                  <div style={{ ...readOnlyBox, whiteSpace: 'pre-wrap', minHeight: 120 }}>
                    {form.description || <span style={{ color: '#64748B' }}>No description.</span>}
                  </div>
                )}
              </Section>
            )}

            {tab === 'History' && !isNew && (
              <Section>
                {/* Comment composer — pinned at top so the user can drop a note
                    without scrolling past the timeline. */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  <input value={newComment} onChange={e => setNewComment(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') postComment(); }}
                    placeholder="Add a comment…  (Enter to post)" style={{ ...inp, flex: 1 }} />
                  <button onClick={postComment} disabled={!newComment.trim()} style={btnAdd}>Post</button>
                </div>

                {/* Timeline — newest first. Mixes notes and field-change events. */}
                {historyLoading && <div style={{ color: '#94A3B8', padding: 8 }}>Loading…</div>}
                {!historyLoading && history.length === 0 && (
                  <div style={{ color: '#64748B', fontSize: 12.5, padding: 8 }}>
                    No history yet. Edits to this opportunity and any comments you post will show here.
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {history.map(h => <HistoryRow key={h.id} h={h} />)}
                </div>
              </Section>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
function HistoryRow({ h }) {
  // audit_log row shape: { action, new_data, created_at, user_name, user_email }
  // action examples we render: 'note' (user comment), 'create_opportunity',
  // 'update_opportunity', 'create_lead' (when seeded from a lead), etc.
  const isNote = h.action === 'note';
  const text = (() => {
    if (isNote) return h.new_data?.text || '(empty comment)';
    if (h.action === 'create_opportunity') return 'Created the opportunity';
    if (h.action === 'update_opportunity') return 'Updated the opportunity';
    return h.action.replace(/_/g, ' ');
  })();
  const ts = h.created_at ? new Date(h.created_at) : null;
  const tsStr = ts ? ts.toLocaleString() : '';
  const author = h.user_name && h.user_name.trim() ? h.user_name : (h.user_email || 'Unknown');
  return (
    <div style={{
      padding: '10px 12px', borderRadius: 10,
      border: '1px solid rgba(255,255,255,0.08)',
      background: isNote ? 'rgba(0,224,255,0.05)' : 'rgba(255,255,255,0.03)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: isNote ? '#6FE6FF' : '#A78BFA', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {isNote ? 'Comment' : 'Change'}
        </span>
        <span style={{ fontSize: 11, color: '#64748B' }}>{author} · {tsStr}</span>
      </div>
      <div style={{ fontSize: 13, color: '#E5E7EB', whiteSpace: 'pre-wrap' }}>{text}</div>
    </div>
  );
}

function ListRow({ opp, customer, selected, onClick }) {
  const value = opp.estimated_value != null
    ? formatCurrency(opp.estimated_value, opp.currency || 'INR')
    : '';
  return (
    <div onClick={onClick} style={{
      padding: '12px 14px', cursor: 'pointer',
      borderLeft: selected ? '3px solid #00E0FF' : '3px solid transparent',
      background: selected ? 'rgba(0,224,255,0.06)' : 'transparent',
      borderBottom: '1px solid rgba(255,255,255,0.04)',
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: selected ? '#6FE6FF' : '#E5E7EB' }}>{opp.name || '(untitled)'}</div>
      <div style={{ fontSize: 11.5, color: '#94A3B8', marginTop: 2 }}>{customer || '—'}</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
        <StagePill stage={opp.stage} />
        {value && <span style={{ fontSize: 11, color: '#CBD5E1' }}>{value}</span>}
      </div>
    </div>
  );
}

function StagePill({ stage }) {
  let color = '#94A3B8', bg = 'rgba(148,163,184,0.15)', border = 'rgba(148,163,184,0.40)';
  if (stage === 'Closed Won') { color = '#10B981'; bg = 'rgba(16,185,129,0.15)'; border = 'rgba(16,185,129,0.40)'; }
  else if (stage === 'Closed Lost') { color = '#FB7185'; bg = 'rgba(244,63,94,0.15)'; border = 'rgba(244,63,94,0.40)'; }
  else if (stage === 'Negotiation') { color = '#34D399'; bg = 'rgba(52,211,153,0.15)'; border = 'rgba(52,211,153,0.40)'; }
  else if (stage === 'Proposal') { color = '#F59E0B'; bg = 'rgba(245,158,11,0.15)'; border = 'rgba(245,158,11,0.40)'; }
  else if (stage === 'Discovery') { color = '#A78BFA'; bg = 'rgba(167,139,250,0.15)'; border = 'rgba(167,139,250,0.40)'; }
  else if (stage === 'Qualification') { color = '#6FE6FF'; bg = 'rgba(0,224,255,0.10)'; border = 'rgba(0,224,255,0.40)'; }
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999,
      color, background: bg, border: `1px solid ${border}`, textTransform: 'uppercase', letterSpacing: 0.4,
    }}>{stage || '—'}</span>
  );
}

function Section({ children }) {
  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 16, marginBottom: 14 }}>
      {children}
    </div>
  );
}
function Grid2({ children }) { return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>{children}</div>; }
function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 10.5, color: '#94A3B8', letterSpacing: '0.4px', textTransform: 'uppercase', fontWeight: 600 }}>{label}</span>
      {children}
    </label>
  );
}
function ReadOnly({ children }) {
  return <div style={readOnlyBox}>{children}</div>;
}

const readOnlyBox = {
  padding: '8px 10px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.05)',
  background: 'rgba(15,23,42,0.4)', color: '#E5E7EB', fontSize: 13,
};
const inp = {
  padding: '8px 10px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.10)',
  background: 'rgba(15,23,42,0.6)', color: '#E5E7EB', fontSize: 13, outline: 'none', width: '100%',
};
const btnAdd = {
  padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
  border: '1px solid rgba(0,224,255,0.40)', background: 'rgba(0,224,255,0.10)', color: '#6FE6FF', cursor: 'pointer',
};
const btnPrimary = {
  padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
  background: 'linear-gradient(135deg, #FF1FA0, #B61F8E)', color: '#fff',
};
const btnSecondary = {
  padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
  border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.04)', color: '#E5E7EB', cursor: 'pointer',
};
const btnDanger = {
  padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
  border: '1px solid rgba(244,63,94,0.40)', background: 'rgba(244,63,94,0.10)', color: '#FB7185', cursor: 'pointer',
};
